import React, { useMemo } from "react";

/**
 * ReactDiffViewer
 * ------------------------------------------------------------
 * A self-contained, dependency-free diff viewer that renders a
 * side-by-side table of "old" vs "new" code and highlights changes.
 *
 * Props:
 * - oldValue: string
 * - newValue: string
 * - showLineNumbers?: boolean (default: true)
 * - wrapLongLines?: boolean (default: false)  // if true, long lines wrap; otherwise horizontal scroll
 *
 * Usage:
 * <ReactDiffViewer oldValue={leftNotes} newValue={rightNotes} />
 */
export default function ReactDiffViewer({
    oldValue = "",
    newValue = "",
    showLineNumbers = true,
    wrapLongLines = false,
}) {
    const aLines = useMemo(() => toLines(oldValue), [oldValue]);
    const bLines = useMemo(() => toLines(newValue), [newValue]);

    const rows = useMemo(() => buildDiffRows(aLines, bLines), [aLines, bLines]);

    return (
        <div className="rdv-root">
            <style>{baseStyles}</style>

            <div
                className="rdv-table-wrap"
                style={{ overflowX: wrapLongLines ? "hidden" : "auto" }}
            >
                <table className="rdv-table">
                    <thead>
                        <tr>
                            {showLineNumbers && <th className="rdv-ln">#</th>}
                            <th className="rdv-head">Elemento 1</th>
                            {showLineNumbers && <th className="rdv-ln">#</th>}
                            <th className="rdv-head">Elemento 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => (
                            <tr key={idx} className={`rdv-row rdv-${r.type}`}>
                                {showLineNumbers && (
                                    <td className="rdv-ln" title={r.aNo ?? ""}>
                                        {r.aNo ?? ""}
                                    </td>
                                )}
                                <td className="rdv-cell" data-side="old">
                                    <CodeCell
                                        text={r.aText}
                                        highlight={r.type === "replace" ? r.charDiff?.oldSpans : undefined}
                                        wrapLongLines={wrapLongLines}
                                    />
                                </td>

                                {showLineNumbers && (
                                    <td className="rdv-ln" title={r.bNo ?? ""}>
                                        {r.bNo ?? ""}
                                    </td>
                                )}
                                <td className="rdv-cell" data-side="new">
                                    <CodeCell
                                        text={r.bText}
                                        highlight={r.type === "replace" ? r.charDiff?.newSpans : undefined}
                                        wrapLongLines={wrapLongLines}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Legend />
        </div>
    );
}

/* --------------------------- Subcomponents --------------------------- */

function CodeCell({ text, highlight, wrapLongLines }) {
    // If we received precomputed spans (character-level), render those.
    if (highlight && Array.isArray(highlight) && highlight.length) {
        return (
            <pre
                className="rdv-code"
                style={{ whiteSpace: wrapLongLines ? "pre-wrap" : "pre" }}
            >
                {highlight.map((seg, i) =>
                    seg.type === "equal" ? (
                        <span key={i}>{seg.text}</span>
                    ) : seg.type === "del" ? (
                        <span key={i} className="rdv-del">
                            {seg.text}
                        </span>
                    ) : (
                        <span key={i} className="rdv-ins">
                            {seg.text}
                        </span>
                    )
                )}
            </pre>
        );
    }

    // Otherwise just render plain (line-level) content.
    return (
        <pre
            className="rdv-code"
            style={{ whiteSpace: wrapLongLines ? "pre-wrap" : "pre" }}
        >
            {text ?? ""}
        </pre>
    );
}

function Legend() {
    return (
        <div className="rdv-legend">
            <span className="rdv-pill rdv-pill-del" title="Rimosso">Rimosso</span>
            <span className="rdv-pill rdv-pill-ins" title="Aggiunto">Aggiunto</span>
            <span className="rdv-pill rdv-pill-rep" title="Sostituito">Sostituito (dettaglio caratteri)</span>
            <span className="rdv-note">Tab e spazi sono rispettati. Scorri orizzontalmente se necessario.</span>
        </div>
    );
}

/* ------------------------------ Styles ------------------------------ */

const baseStyles = `
/* Namespaced to .rdv-* to avoid clashing */
.rdv-root { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Arial, "Apple Color Emoji","Segoe UI Emoji"; }
.rdv-table-wrap { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; }
.rdv-table { width: 100%; border-collapse: separate; border-spacing: 0; table-layout: fixed; }
.rdv-head { text-align: left; font-weight: 600; font-size: 12px; color: #6b7280; padding: 10px 12px; background: #f9fafb; border-bottom: 1px solid #eef2f7; position: sticky; top: 0; z-index: 1; }
.rdv-ln { width: 56px; text-align: right; font-variant-numeric: tabular-nums; color: #9ca3af; padding: 10px 8px; background: #f9fafb; border-bottom: 1px solid #eef2f7; }
.rdv-row .rdv-ln { border-right: 1px solid #f0f3f7; }
.rdv-cell { padding: 0; vertical-align: top; border-bottom: 1px solid #f3f4f6; }
.rdv-code { margin: 0; padding: 10px 12px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12.5px; line-height: 1.55; tab-size: 2; background: transparent; }
.rdv-row:hover .rdv-code { background: #fafafa; }

.rdv-equal [data-side="old"] .rdv-code,
.rdv-equal [data-side="new"] .rdv-code { background: transparent; }

.rdv-del { background: #ffe6e6; text-decoration: none; }
.rdv-ins { background: #e6ffea; text-decoration: none; }

.rdv-row.rdv-delete [data-side="old"] .rdv-code { background: #fff1f1; }
.rdv-row.rdv-insert [data-side="new"] .rdv-code { background: #f1fff4; }
.rdv-row.rdv-replace [data-side="old"] .rdv-code { background: #fff6f1; }
.rdv-row.rdv-replace [data-side="new"] .rdv-code { background: #f1fff8; }

.rdv-legend { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin: 10px 2px 0; font-size: 12px; color: #6b7280; }
.rdv-pill { display: inline-block; padding: 2px 8px; border-radius: 9999px; border: 1px solid #e5e7eb; background: #f9fafb; }
.rdv-pill-del { background: #ffe6e6; border-color: #ffd1d1; }
.rdv-pill-ins { background: #e6ffea; border-color: #c9ffd4; }
.rdv-pill-rep { background: #fff6dc; border-color: #ffe9ad; }
.rdv-note { margin-left: 4px; }
`;

/* ------------------------------ Diff Core ------------------------------ */

/** Normalize string into array of lines. */
function toLines(str) {
    if (!str && str !== "") return [];
    // Normalize newlines, preserve empty last line if present
    const s = String(str).replace(/\r\n/g, "\n").replace(/\t/g, "  ");
    // Split but keep trailing empty line to align diffs with editors
    const parts = s.split("\n");
    return parts;
}

/** Build side-by-side rows with line- and character-level highlighting. */
function buildDiffRows(aLines, bLines) {
    const ops = lcsLineOps(aLines, bLines);
    const rows = [];

    let aNo = 1;
    let bNo = 1;

    for (let i = 0; i < ops.length; i++) {
        const op = ops[i];

        if (op.type === "equal") {
            for (let k = 0; k < op.count; k++) {
                rows.push({
                    type: "equal",
                    aNo: aNo,
                    bNo: bNo,
                    aText: aLines[aNo - 1],
                    bText: bLines[bNo - 1],
                });
                aNo++;
                bNo++;
            }
            continue;
        }

        // Collect run of deletions and insertions to pair replacements
        const delBuf = [];
        const insBuf = [];

        let j = i;
        while (j < ops.length && (ops[j].type === "delete" || ops[j].type === "insert")) {
            const run = ops[j];
            if (run.type === "delete") {
                for (let k = 0; k < run.count; k++) {
                    delBuf.push(aLines[aNo - 1 + k]);
                }
                aNo += run.count;
            } else {
                for (let k = 0; k < run.count; k++) {
                    insBuf.push(bLines[bNo - 1 + k]);
                }
                bNo += run.count;
            }
            j++;
        }

        // Pair as many as possible as "replace"
        const pairCount = Math.min(delBuf.length, insBuf.length);
        for (let p = 0; p < pairCount; p++) {
            const aText = delBuf[p];
            const bText = insBuf[p];
            rows.push({
                type: "replace",
                aNo: aNo - (delBuf.length - p), // approximate original numbering (not displayed for replaced as we already advanced)
                bNo: bNo - (insBuf.length - p),
                aText,
                bText,
                charDiff: charDiff(aText, bText),
            });
        }

        // Remaining deletions as independent rows
        for (let r = pairCount; r < delBuf.length; r++) {
            rows.push({
                type: "delete",
                aNo: aNo - (delBuf.length - r),
                bNo: null,
                aText: delBuf[r],
                bText: "",
            });
        }

        // Remaining insertions as independent rows
        for (let r = pairCount; r < insBuf.length; r++) {
            rows.push({
                type: "insert",
                aNo: null,
                bNo: bNo - (insBuf.length - r),
                aText: "",
                bText: insBuf[r],
            });
        }

        i = j - 1; // jump to last processed op
    }

    return rows;
}

/** Line-level diff using LCS, returning a compressed list of ops. */
function lcsLineOps(aLines, bLines) {
    const n = aLines.length;
    const m = bLines.length;

    // DP matrix of LCS lengths
    const dp = Array(n + 1);
    for (let i = 0; i <= n; i++) {
        dp[i] = new Uint32Array(m + 1);
    }

    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            dp[i][j] =
                aLines[i] === bLines[j]
                    ? dp[i + 1][j + 1] + 1
                    : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }

    // Reconstruct ops
    const ops = [];
    let i = 0,
        j = 0;

    while (i < n && j < m) {
        if (aLines[i] === bLines[j]) {
            pushOp(ops, "equal", 1);
            i++;
            j++;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            pushOp(ops, "delete", 1);
            i++;
        } else {
            pushOp(ops, "insert", 1);
            j++;
        }
    }
    while (i < n) {
        pushOp(ops, "delete", 1);
        i++;
    }
    while (j < m) {
        pushOp(ops, "insert", 1);
        j++;
    }

    return ops;
}

function pushOp(arr, type, count) {
    const last = arr[arr.length - 1];
    if (last && last.type === type) {
        last.count += count;
    } else {
        arr.push({ type, count });
    }
}

/** Character-level diff for a pair of lines, returns spans to render. */
function charDiff(a, b) {
    const aChars = [...(a ?? "")];
    const bChars = [...(b ?? "")];

    const n = aChars.length;
    const m = bChars.length;

    // Small optimization for equal lines
    if (a === b) {
        return {
            oldSpans: [{ type: "equal", text: a }],
            newSpans: [{ type: "equal", text: b }],
        };
    }

    // DP for LCS over characters
    const dp = Array(n + 1);
    for (let i = 0; i <= n; i++) {
        dp[i] = new Uint16Array(m + 1);
    }

    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            dp[i][j] =
                aChars[i] === bChars[j]
                    ? dp[i + 1][j + 1] + 1
                    : dp[i + 1][j] > dp[i][j + 1]
                        ? dp[i + 1][j]
                        : dp[i][j + 1];
        }
    }

    // Mark chars that belong to LCS
    const keepA = new Array(n).fill(false);
    const keepB = new Array(m).fill(false);

    let i = 0,
        j = 0;
    while (i < n && j < m) {
        if (aChars[i] === bChars[j]) {
            keepA[i] = true;
            keepB[j] = true;
            i++;
            j++;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            i++;
        } else {
            j++;
        }
    }

    // Convert to spans
    const oldSpans = compressSpans(aChars, keepA, "del");
    const newSpans = compressSpans(bChars, keepB, "ins");

    return { oldSpans, newSpans };
}

function compressSpans(chars, keepMask, changeType) {
    const spans = [];
    let buf = "";
    let mode = "equal"; // 'equal' | changeType

    const flush = () => {
        if (!buf) return;
        spans.push({ type: mode, text: buf });
        buf = "";
    };

    for (let i = 0; i < chars.length; i++) {
        const shouldKeep = keepMask[i];
        const targetMode = shouldKeep ? "equal" : changeType;
        if (targetMode !== mode) {
            flush();
            mode = targetMode;
        }
        buf += chars[i];
    }
    flush();
    return spans;
}